// frontend/src/features/tbs/TBSPage.jsx
// TBS — Tax Bill Scraper. Builds a run from pasted/uploaded accounts, starts
// it as a persisted background job, and shows live progress with the tax
// year and account verification for every statement.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppWindow, LogOut, AlertCircle, Archive } from "lucide-react";

import "./tbs.css";
import * as tbsApi from "./tbsApi";
import RunBuilder from "./components/RunBuilder";
import PreviewTable from "./components/PreviewTable";
import JobPanel from "./components/JobPanel";
import JobHistory from "./components/JobHistory";
import CollectorCatalog from "./components/CollectorCatalog";

const POLL_MS = 2500;

export default function TBSPage({ onClose, logout }) {
  const navigate = useNavigate();
  const isAdmin = (() => { try { return localStorage.getItem("is_admin") === "1"; } catch { return false; } })();

  // Catalog
  const [collectors, setCollectors] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [maxRows, setMaxRows] = useState(2500);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [probing, setProbing] = useState(false);
  const collectorsByKey = useMemo(() => Object.fromEntries(collectors.map((c) => [c.key, c])), [collectors]);

  // Builder
  const [defaultCollector, setDefaultCollector] = useState("");
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [scrapeData, setScrapeData] = useState(false);
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [starting, setStarting] = useState(false);

  // Jobs
  const [jobs, setJobs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [job, setJob] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  const loadCollectors = useCallback(async () => {
    try {
      const d = await tbsApi.getCollectors();
      setCollectors(d.collectors || []);
      if (d.current_year) setCurrentYear(d.current_year);
      if (d.max_rows_per_run) setMaxRows(d.max_rows_per_run);
    } catch (e) { setError(tbsApi.errorText(e, "Could not load the collector list")); }
  }, []);

  const loadJobs = useCallback(async () => {
    try { setJobs(await tbsApi.listJobs(40)); } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadCollectors(); loadJobs(); }, [loadCollectors, loadJobs]);

  // Poll the active job while it is running
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!activeId) { setJob(null); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const j = await tbsApi.getJob(activeId);
        if (cancelled) return;
        setJob(j);
        if (!(j.status === "running" || j.status === "queued") && pollRef.current) {
          clearInterval(pollRef.current); pollRef.current = null; loadJobs();
        }
      } catch (e) {
        if (!cancelled) setError(tbsApi.errorText(e, "Could not load the run"));
      }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeId, loadJobs]);

  const scrapeSupported = useMemo(() => {
    const keys = new Set();
    (preview?.rows || []).forEach((r) => { if (r.ok && collectorsByKey[r.collector]?.scrape_data) keys.add(collectorsByKey[r.collector].name); });
    if (!preview && defaultCollector && collectorsByKey[defaultCollector]?.scrape_data) keys.add(collectorsByKey[defaultCollector].name);
    return [...keys];
  }, [preview, defaultCollector, collectorsByKey]);

  async function check() {
    setChecking(true); setError("");
    try {
      const p = await tbsApi.previewInput({ text, file, defaultCollector });
      setPreview(p);
      if (p.count === 0) setError("No accounts were found in what you pasted or uploaded.");
    } catch (e) { setError(tbsApi.errorText(e, "Could not check the accounts")); }
    finally { setChecking(false); }
  }

  function removePreviewRow(row) {
    setPreview((p) => p ? { ...p, rows: p.rows.filter((r) => r !== row), count: p.count - 1 } : p);
  }

  async function start() {
    const rows = (preview?.rows || []).filter((r) => r.ok).map((r) => ({ collector: r.collector, account: r.account, year: r.year || undefined, label: r.label || undefined }));
    if (!rows.length) return;
    if (rows.length > maxRows) {
      setError(`A run is capped at ${maxRows.toLocaleString()} accounts (this list has ${rows.length.toLocaleString()}). Split it into smaller files; runs are independent and can go at the same time.`);
      return;
    }
    setStarting(true); setError("");
    try {
      const j = await tbsApi.createJob({ rows, tax_year: taxYear || null, scrape_data: scrapeData, label: label || null, source_filename: file?.name || null });
      setPreview(null); setText(""); setFile(null); setLabel("");
      setActiveId(j.id); setJob(j);
      loadJobs();
    } catch (e) { setError(tbsApi.errorText(e, "Could not start the run")); }
    finally { setStarting(false); }
  }

  async function act(fn, after) {
    setActionBusy(true); setError("");
    try { await fn(); if (after) await after(); }
    catch (e) { setError(tbsApi.errorText(e)); }
    finally { setActionBusy(false); }
  }

  const refreshJob = async () => { if (activeId) setJob(await tbsApi.getJob(activeId)); await loadJobs(); };

  async function onDelete() {
    if (!job) return;
    const okCount = job.ok_count || 0;
    if (!window.confirm(`Delete "${job.label || `Run #${job.id}`}" and its ${okCount} stored statement${okCount === 1 ? "" : "s"}? This cannot be undone.`)) return;
    await act(() => tbsApi.deleteJob(job.id), async () => { setActiveId(null); await loadJobs(); });
  }

  async function onProbe() {
    setProbing(true);
    try { await tbsApi.runProbe(false); setTimeout(async () => { await loadCollectors(); setProbing(false); }, 25000); }
    catch (e) { setError(tbsApi.errorText(e)); setProbing(false); }
  }

  const builderBusy = false; // runs are independent background jobs; the builder stays usable while one runs

  return (
    <div className="tbs">
      <header className="tbs-hdr">
        <div className="tbs-hdr-l">
          <img src="/assets/balogo.png" alt="" className="tbs-logo" />
          <span className="tbs-brand">BA Property Tax</span>
          <span className="tbs-sep" />
          <span className="tbs-app">TBS</span>
          <span className="tbs-app-sub">Tax Bill Scraper</span>
        </div>
        <div className="tbs-hdr-r">
          <button className="tbs-hbtn" onClick={() => setCatalogOpen(true)}>Collectors</button>
          <button className="tbs-hbtn" onClick={() => (onClose ? onClose() : navigate("/apps"))}><AppWindow size={13} /> Apps</button>
          {logout && <button className="tbs-hbtn out" onClick={logout} aria-label="Log out"><LogOut size={13} /></button>}
        </div>
      </header>

      <div className="tbs-body">
        <aside className="tbs-rail">
          <RunBuilder
            collectors={collectors} currentYear={currentYear} busy={builderBusy}
            defaultCollector={defaultCollector} onDefaultCollector={(k) => { setDefaultCollector(k); setPreview(null); }} onBrowse={() => setCatalogOpen(true)}
            taxYear={taxYear} onTaxYear={setTaxYear}
            text={text} onText={(t) => { setText(t); setPreview(null); }}
            file={file} onFile={(f) => { setFile(f); setPreview(null); }}
            label={label} onLabel={setLabel}
            scrapeData={scrapeData} onScrapeData={setScrapeData} scrapeSupported={scrapeSupported}
            preview={preview} onCheck={check} onStart={start} onClearPreview={() => setPreview(null)}
            checking={checking} starting={starting}
          />
          <JobHistory jobs={jobs} activeId={activeId} onSelect={(id) => { setActiveId(id); setError(""); }} onNew={() => setActiveId(null)} />
        </aside>

        <main className="tbs-main">
          {error && <div className="tbs-err"><AlertCircle size={14} /> <span>{error}</span><button className="tbs-x" style={{ marginLeft: "auto" }} onClick={() => setError("")}>×</button></div>}

          {activeId && job ? (
            <JobPanel
              job={job} collectorsByKey={collectorsByKey} actionBusy={actionBusy}
              onCancel={() => act(() => tbsApi.cancelJob(job.id), refreshJob)}
              onRetry={() => act(() => tbsApi.retryJob(job.id), refreshJob)}
              onDelete={onDelete}
              onZip={() => act(() => tbsApi.downloadZip(job.id))}
              onZipYear={() => act(() => tbsApi.downloadZip(job.id, job.tax_year))}
              onData={() => act(() => tbsApi.downloadData(job.id))}
              onOpenPdf={(r) => act(() => tbsApi.openRowPdf(job.id, r.id))}
              onDownloadPdf={(r) => act(() => tbsApi.downloadRowPdf(job.id, r.id, r.filename))}
            />
          ) : preview ? (
            <PreviewTable preview={preview} collectorsByKey={collectorsByKey} onRemove={removePreviewRow} />
          ) : (
            <div className="tbs-card tbs-empty" style={{ flex: 1 }}>
              <Archive size={34} color="#C2C5CE" strokeWidth={1.5} />
              <h3>No run selected</h3>
              <p>Pick a collector, paste or upload the accounts, and check them. Runs keep going if you close the tab, and every statement is checked for the account and tax year it is actually for.</p>
            </div>
          )}
        </main>
      </div>

      <CollectorCatalog open={catalogOpen} onClose={() => setCatalogOpen(false)} collectors={collectors}
        onPick={(c) => { setDefaultCollector(c.key); setPreview(null); }} isAdmin={isAdmin} onProbe={onProbe} probing={probing} />
    </div>
  );
}
